import {
  AddressInput,
  Button,
  Checkbox,
  CountryInput,
  DatePicker,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Input,
  InputErrorMessage,
  Label,
  LoadingIndicator,
  PhoneInput,
  SingleFileUploader,
} from '@commenda/kakashi';
import { defaultAddressSchema } from '@commenda/schema/address';
import { IAddress, IncorporationRequestDataType } from '@commenda/types';
import { FileBasic } from '@commenda/types/src/file/types';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { classNames, fetcher } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { CorporationType, LegacyDocumentType } from '@prisma/client';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { KeyedMutator } from 'swr';
import { DirectorFormType } from '../Director/DirectorFormType';
import { getIdentityProof } from '../in/utils';
import { directorSchema, DirectorSchemaFields } from './schema';

const DirectorForm: DirectorFormType<DirectorBase> = ({
  director,
  companyDataMutate,
  setOpen,
  isAddingDirector,
  setIsAddingDirector,
  companyId,
  corporationId,
  token,
  setNotification,
  onErrorResolve,
  selectedDirectorIds,
  setSelectedDirectorIds,
  modalConfig,
  workRequestData,
}: {
  director?: DirectorBase;
  companyDataMutate: KeyedMutator<unknown>;
  isAddingDirector: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAddingDirector: React.Dispatch<React.SetStateAction<boolean>>;
  companyId: number;
  corporationId?: number;
  token: string;
  setNotification: TSetNotification;
  onErrorResolve?: () => void;
  selectedDirectorIds?: number[];
  setSelectedDirectorIds?: React.Dispatch<React.SetStateAction<number[]>>;
  modalConfig?: {
    heading?: string;
    dialogTitle?: string;
    isDirector?: boolean;
    isAuthorizedSignatory?: boolean;
    postSave?: (director: {
      id: number;
      directorFirstName: string;
      directorLastName: string;
    }) => void;
  };
  workRequestData?: WorkRequestResponseType & {
    requestData: IncorporationRequestDataType;
  };
}) => {
  const passportProofFile = getIdentityProof(director, corporationId);
  const [passportProof, setPassportProof] = useState<FileBasic | null>(
    passportProofFile
  );
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    setValue,
    watch,
    clearErrors,
  } = useForm<DirectorSchemaFields>({
    resolver: zodResolver(directorSchema),
    defaultValues: {
      dateOfBirth: director?.dateOfBirth || '',
      directorFirstName: director?.directorFirstName,
      directorLastName: director?.directorLastName,
      email: director?.email,
      number: director?.number,
      personalDetails: {
        nationality: director?.personalDetails?.nationality,
      },
      // we are using the default address schema so that zod can generate error states for all the address fields.
      // Else, zod generates error like {operatingAddress: Expected Object....}, we wont be able to show error states in the UI properly.
      operatingAddress: director?.operatingAddress || defaultAddressSchema,
      passportProof: passportProofFile,
      corporationHasDirector: [
        {
          corporationId,
          additionalDirectorInformation: {
            isAuthorizedSignatory:
              director?.corporationHasDirector?.find(
                (cd) =>
                  cd.corporationId === corporationId &&
                  cd.directorId === director.id
              )?.additionalDirectorInformation?.isAuthorizedSignatory || false,
            isManager:
              director?.corporationHasDirector?.find(
                (cd) =>
                  cd.corporationId === corporationId &&
                  cd.directorId === director.id
              )?.additionalDirectorInformation?.isManager || false,
          },
        },
      ],
    },
  });

  useEffect(() => {
    if (passportProof) {
      setValue('passportProof', passportProof);
    }
  }, [passportProof]);

  const onSubmit = async (data: DirectorSchemaFields) => {
    setIsAddingDirector(true);

    // Extract workRequestId from workRequestData prop
    const workRequestId = workRequestData?.id;

    const body = {
      directorFirstName: data.directorFirstName,
      directorLastName: data.directorLastName,
      dateOfBirth: data.dateOfBirth,
      number: data.number,
      email: data.email,
      personalDetails: {
        nationality: data?.personalDetails?.nationality,
      },
      companyId,
      operatingAddress: data.operatingAddress,
      filesToAdd: [
        {
          // Use existing file ID if available, otherwise use -1 for new uploads
          id: passportProof?.id ?? passportProofFile?.id ?? -1,
          fileName: passportProof?.fileName,
          fileUrl: passportProof?.fileUrl,
          documentType: LegacyDocumentType.Passport,
        },
      ],
      corporationHasDirector: [
        {
          corporationId,
          additionalDirectorInformation: {
            ...(modalConfig?.isDirector ? { isDirector: true } : {}),
            ...(modalConfig?.isAuthorizedSignatory
              ? {
                  isAuthorizedSignatory: true,
                }
              : {
                  isAuthorizedSignatory:
                    data?.corporationHasDirector?.[0]
                      .additionalDirectorInformation.isAuthorizedSignatory,
                }),
            ...(data?.corporationHasDirector?.[0]?.additionalDirectorInformation
              ?.isManager
              ? {
                  isManager: true,
                }
              : {}),
          },
        },
      ],
      // Include wrkReqId if it exists (workRequestData.id is typed as string)
      ...(workRequestId ? { wrkReqId: workRequestId } : {}),
    };

    const res = await fetcher(
      director?.id ? `director/${director.id}` : 'director',
      {
        method: 'POST',
        body,
      },
      token
    );

    if (!res.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong, please try again later or contact team Commenda if you need help',
      });

      await companyDataMutate();
      setIsAddingDirector(false);
      return;
    }

    if (res.status === 409) {
      const error = await res.json();
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: error.message,
      });
      setIsAddingDirector(false);
      return;
    }
    await companyDataMutate();
    const resData = await res.json();
    if (setSelectedDirectorIds) {
      setSelectedDirectorIds([...(selectedDirectorIds || []), resData.id]);
    }
    if (modalConfig?.postSave) {
      modalConfig.postSave({
        id: resData.id,
        directorFirstName: resData.directorFirstName,
        directorLastName: resData.directorLastName,
      });
    }
    if (onErrorResolve) onErrorResolve();
    setIsAddingDirector(false);
    reset();
    setOpen(false);
  };

  return (
    <form
      className="flex flex-col max-w-[574px]"
      onSubmit={handleSubmit(onSubmit, (err) => {
        console.error(err);
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: 'Please enter all the required details.',
        });
      })}
    >
      <DialogHeader>
        <DialogTitle>
          {director
            ? modalConfig?.dialogTitle || 'Edit director'
            : modalConfig?.heading ||
              modalConfig?.dialogTitle ||
              'Add a new director'}
        </DialogTitle>
      </DialogHeader>
      <div className="grow shrink-0 flex flex-col gap-4 overflow-y-auto max-h-[calc(85vh-172px)] py-3 px-3">
        <div className="flex flex-col gap-4">
          <FieldWrapper>
            <Label className="text-sm gap-0.5 text-neutral-1">
              <span>Legal Name (as on your passport)</span>
              <span>*</span>
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="First name"
                {...register('directorFirstName')}
                error={errors?.directorFirstName?.message}
                defaultValue={watch('directorFirstName')}
              />
              <Input
                placeholder="Last name"
                {...register('directorLastName')}
                error={errors?.directorFirstName?.message}
                defaultValue={watch('directorLastName')}
              />
            </div>
          </FieldWrapper>
          <div className="grid grid-cols-2 gap-2">
            <FieldWrapper>
              <Label className="text-sm text-neutral-1">
                Phone number{' '}
                <span className="text-text-violet-tertiary ml-1">*</span>
              </Label>

              <PhoneInput
                id="phoneNumber"
                placeholder=""
                className="h-[42px]"
                {...register('number')}
                onChange={(e) => {
                  setValue('number', e);
                  clearErrors('number');
                }}
                value={watch('number')}
                error={errors?.number?.message}
              />
            </FieldWrapper>

            <FieldWrapper>
              <Label className="text-sm text-neutral-1">
                Email <span className="text-violet-solid ml-1">*</span>
              </Label>

              <Input
                type="email"
                placeholder="Email"
                {...register('email')}
                error={errors?.email?.message}
                value={watch('email')}
              />
            </FieldWrapper>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FieldWrapper>
              <Label className="text-sm text-neutral-1">
                Date of birth <span className="text-violet-solid ml-1">*</span>
              </Label>

              <DatePicker
                hideCalendar
                date={watch('dateOfBirth')}
                setDate={(dt) => {
                  clearErrors('dateOfBirth');
                  setValue('dateOfBirth', dt);
                }}
                _triggerClassName="min-w-fit w-full h-[42px]"
                hasError={!!errors?.dateOfBirth?.message}
                {...register('dateOfBirth')}
                disabled={{
                  after: new Date(),
                }}
              />
              {errors?.dateOfBirth?.message ? (
                <InputErrorMessage message={errors?.dateOfBirth?.message} />
              ) : null}
            </FieldWrapper>

            <FieldWrapper>
              <Label className="text-sm text-neutral-1">
                Nationality<span className="text-violet-solid ml-1">*</span>
              </Label>

              <CountryInput
                _className="h-[42px]"
                placeholder="Search country"
                type="single"
                id="nationality"
                onValueChange={(ctr) => {
                  setValue('personalDetails.nationality', ctr);
                }}
                {...register('personalDetails.nationality')}
                error={
                  errors?.personalDetails?.nationality?.message &&
                  !watch('personalDetails.nationality')
                    ? errors?.personalDetails?.nationality?.message
                    : undefined
                }
                value={watch('personalDetails.nationality')}
              />
            </FieldWrapper>
          </div>
        </div>

        <FieldWrapper>
          <Label>
            Current Address <span className="text-violet-solid ml-1">*</span>
          </Label>
          <AddressInput
            address={watch('operatingAddress') as IAddress}
            onChange={(ad) => {
              setValue('operatingAddress', ad);
              if (ad.country !== watch('operatingAddress.country')) {
                clearErrors('operatingAddress.state');
              }
            }}
            lastRowClassName="grid-cols-2"
            error={{
              country:
                errors?.operatingAddress?.country?.message &&
                !watch('operatingAddress.country')
                  ? errors?.operatingAddress?.country?.message
                  : undefined,
              addressLine1:
                !watch('operatingAddress.addressLine1') &&
                errors?.operatingAddress?.addressLine1?.message
                  ? errors?.operatingAddress?.addressLine1?.message
                  : null,
              addressLine2:
                !watch('operatingAddress.addressLine2') &&
                errors?.operatingAddress?.addressLine2?.message
                  ? errors?.operatingAddress?.addressLine2?.message
                  : null,
              city:
                !watch('operatingAddress.city') &&
                errors?.operatingAddress?.city?.message
                  ? errors?.operatingAddress?.city?.message
                  : null,
              postalCode:
                !watch('operatingAddress.postalCode') &&
                errors?.operatingAddress?.postalCode?.message
                  ? errors?.operatingAddress?.postalCode?.message
                  : null,
              state:
                !watch('operatingAddress.state') &&
                errors?.operatingAddress?.state?.message
                  ? errors?.operatingAddress?.state?.message
                  : undefined,
            }}
          />
        </FieldWrapper>

        <FieldWrapper>
          <Label>
            Upload Passport
            <span className="text-violet-solid ml-1">*</span>
          </Label>
          <SingleFileUploader
            companyId={companyId}
            setFile={(file) => {
              setPassportProof(file);
              clearErrors('passportProof');
            }}
            file={passportProof}
            error={errors?.passportProof?.message}
            setError={(msg: string) =>
              setError('passportProof', { type: 'custom', message: msg })
            }
            token={token}
            accept={{
              'application/pdf': ['.pdf'],
              'image/*': ['.png', '.jpg', '.jpeg'],
            }}
            maxSize={10 * 1024 * 1024} // 10MB
            documentType={LegacyDocumentType.Passport}
            _className="w-full"
          />
          {errors?.passportProof ||
          errors?.passportProof?.fileName ||
          errors?.passportProof?.fileUrl ? (
            <InputErrorMessage message="Please upload your passport." />
          ) : null}
        </FieldWrapper>

        {!modalConfig?.isAuthorizedSignatory ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-1">
              <Checkbox
                id="isAuthorizedSignatory"
                {...register(
                  'corporationHasDirector.0.additionalDirectorInformation.isAuthorizedSignatory'
                )}
                defaultChecked={
                  !!watch(
                    'corporationHasDirector.0.additionalDirectorInformation.isAuthorizedSignatory'
                  )
                }
                onCheckedChange={(val) => {
                  if (val) {
                    setValue(
                      'corporationHasDirector.0.additionalDirectorInformation.isAuthorizedSignatory',
                      true
                    );
                  } else {
                    setValue(
                      'corporationHasDirector.0.additionalDirectorInformation.isAuthorizedSignatory',
                      false
                    );
                  }
                }}
              />
              <Label htmlFor="isAuthorizedSignatory">
                This person is an authorized signatory.
              </Label>
            </div>
            {workRequestData?.requestData?.corporationType ===
              CorporationType.LLC && (
              <div className="flex items-center gap-1">
                <Checkbox
                  id="isManager"
                  {...register(
                    'corporationHasDirector.0.additionalDirectorInformation.isManager'
                  )}
                  defaultChecked={
                    !!watch(
                      'corporationHasDirector.0.additionalDirectorInformation.isManager'
                    )
                  }
                  onCheckedChange={(val) => {
                    if (val) {
                      setValue(
                        'corporationHasDirector.0.additionalDirectorInformation.isManager',
                        true
                      );
                    } else {
                      setValue(
                        'corporationHasDirector.0.additionalDirectorInformation.isManager',
                        false
                      );
                    }
                  }}
                />
                <Label htmlFor="isManager">This person is a manager.</Label>
              </div>
            )}
          </div>
        ) : null}
      </div>
      <DialogFooter>
        <Button
          type="button"
          variant="secondary"
          onClick={() => setOpen(false)}
        >
          <span>Cancel</span>
        </Button>
        <Button type="submit" variant="primary" disabled={isAddingDirector}>
          {isAddingDirector ? <LoadingIndicator size={32} /> : null}
          <span
            className={classNames(
              'flex items-center',
              isAddingDirector ? 'ml-2' : ''
            )}
          >
            Save
          </span>
        </Button>
      </DialogFooter>
    </form>
  );
};

export default DirectorForm;
